import React, { useCallback, useRef, useState } from "react";
import { UploadCloud, Cloud, HardDrive } from "lucide-react";
import { toast } from "sonner";

export default function UploadZone({ onFile }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFiles = useCallback(
    (files) => {
      const f = files?.[0];
      if (!f) return;
      if (!f.type.startsWith("video/")) {
        toast.error("Solo se aceptan archivos de video", { description: f.name });
        return;
      }
      onFile(f);
    },
    [onFile],
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="w-full max-w-3xl mx-auto p-8 slide-up">
      <div className="mb-8 text-center">
        <span className="inline-block text-[11px] tracking-[0.3em] uppercase text-[#FFC82E] font-bold mb-3">
          Etapa 01 · Carga aislada
        </span>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
          Elimina el logo.{" "}
          <span className="text-flag">Conserva el video.</span>
        </h1>
        <p className="text-[#A1A1AA] mt-3 max-w-xl mx-auto text-sm sm:text-base">
          Arrastra tu archivo. El primer fotograma se carga{" "}
          <span className="text-[#FDFDFD]">localmente</span> via{" "}
          <code className="font-mono-pro text-[#FFC82E]">
            URL.createObjectURL
          </code>
          . Nada se sube hasta que presiones <b>Procesar</b>.
        </p>
      </div>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        data-testid="upload-dropzone"
        className={`w-full border-2 border-dashed rounded-md py-16 px-6 flex flex-col items-center justify-center transition-colors ${
          drag
            ? "border-[#E01F26] bg-[#1A1A1A]"
            : "border-[#27272A] hover:border-[#E01F26] hover:bg-[#141414]"
        }`}
      >
        <UploadCloud
          size={42}
          strokeWidth={1.25}
          className={drag ? "text-[#E01F26]" : "text-[#A1A1AA]"}
        />
        <span className="mt-4 font-display font-bold text-lg">
          Arrastra y suelta tu video aquí
        </span>
        <span className="text-xs text-[#71717A] mt-1 tracking-wide">
          o haz click para seleccionar · mp4, mov, mkv, webm
        </span>
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          hidden
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="upload-input"
        />
      </button>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <button
          type="button"
          onClick={() =>
            toast.info("Google Drive", {
              description:
                "Conecta tu Drive desde Configuración (OAuth no incluido en modo local).",
            })
          }
          data-testid="cloud-import-gdrive"
          className="w-full border border-[#27272A] bg-[#141414] hover:bg-[#1A1A1A] text-sm py-2.5 rounded-sm flex items-center justify-center gap-2 transition-colors"
        >
          <Cloud size={15} strokeWidth={1.5} />
          <span className="text-[#A1A1AA]">Importar desde Google Drive</span>
        </button>
        <button
          type="button"
          onClick={() =>
            toast.info("Dropbox", {
              description:
                "Conecta tu Dropbox desde Configuración (OAuth no incluido en modo local).",
            })
          }
          data-testid="cloud-import-dropbox"
          className="w-full border border-[#27272A] bg-[#141414] hover:bg-[#1A1A1A] text-sm py-2.5 rounded-sm flex items-center justify-center gap-2 transition-colors"
        >
          <HardDrive size={15} strokeWidth={1.5} />
          <span className="text-[#A1A1AA]">Importar desde Dropbox</span>
        </button>
      </div>

      <div className="mt-10 grid grid-cols-3 gap-4 text-center">
        {[
          { k: "01", t: "Carga local", s: "Object URL en navegador" },
          { k: "02", t: "Dibuja ROI", s: "Rectángulo sobre el logo" },
          { k: "03", t: "FFmpeg delogo", s: "Interpolación espacial" },
        ].map((step) => (
          <div
            key={step.k}
            className="border border-[#27272A] bg-[#0A0A0A] p-4 rounded-sm"
          >
            <div className="font-mono-pro text-xs text-[#FFC82E] mb-1">
              {step.k}
            </div>
            <div className="font-display font-bold text-sm">{step.t}</div>
            <div className="text-xs text-[#71717A] mt-1">{step.s}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
